import { QueryClient } from "@tanstack/react-query";
import type { TavusFace } from "./client";

type FacesResponse = { faces: TavusFace[] };

/**
 * Shared TanStack Query key for the Tavus faces catalog. `useTavusFaces`
 * and `getCachedTavusFaceThumbnail` both read this key so any component
 * that has primed the cache lets others resolve a face thumbnail
 * synchronously — no per-card network calls.
 */
export const TAVUS_FACES_QUERY_KEY = ["tavus", "faces"] as const;

let queryClientRef: QueryClient | null = null;

/** Called once from `App.tsx` (or the QueryClientProvider owner). */
export function registerTavusQueryClient(client: QueryClient) {
  queryClientRef = client;
}

function extractThumb(f: Record<string, unknown>): string | undefined {
  const nested = f.thumbnail;
  const nestedUrl =
    nested && typeof nested === "object"
      ? ((nested as { image_url?: string; url?: string }).image_url ??
          (nested as { image_url?: string; url?: string }).url)
      : typeof nested === "string"
        ? (nested as string)
        : undefined;
  const video = typeof f.thumbnail_video_url === "string" ? (f.thumbnail_video_url as string) : undefined;
  const isImage = (u?: string) => !!u && !/\.(mp4|webm|mov)(\?|$)/i.test(u);
  return (
    (typeof f.thumbnail_image_url === "string" && (f.thumbnail_image_url as string)) ||
    (typeof f.thumbnail_url === "string" && (f.thumbnail_url as string)) ||
    (typeof nestedUrl === "string" && nestedUrl) ||
    (typeof f.gif_url === "string" && (f.gif_url as string)) ||
    (isImage(video) ? video : undefined) ||
    undefined
  );
}

/**
 * Read a face thumbnail from the query cache. Returns `undefined` when
 * the cache hasn't been primed yet — callers should fall back gracefully.
 */
export function getCachedTavusFaceThumbnail(faceId: string): string | undefined {
  const qc = queryClientRef;
  if (!qc) return undefined;
  const data = qc.getQueryData<FacesResponse>(TAVUS_FACES_QUERY_KEY);
  const faces = data?.faces ?? [];
  const match = faces.find(
    (f: TavusFace) => String(f.face_id || f.replica_id || "") === faceId,
  );
  if (!match) return undefined;
  return extractThumb(match as Record<string, unknown>);
}